import hljs from 'highlight.js/lib/core'

class UndefinedLanguageError extends Error {
  constructor() {
    super('Unsupported notebook language')
  }
}

const codeElementSelector = 'div.cm-editor'

const defaultLang = 'python'
const commonLanguages = {
  python: () => import('highlight.js/lib/languages/python'),
  ruby: () => import('highlight.js/lib/languages/ruby'),
  javascript: () => import('highlight.js/lib/languages/javascript'),
  bash: () => import('highlight.js/lib/languages/bash'),
  sql: () => import('highlight.js/lib/languages/sql'),
  julia: () => import('highlight.js/lib/languages/julia'),
  perl: () => import('highlight.js/lib/languages/perl'),
  r: () => import('highlight.js/lib/languages/r'),
}

function needsHighlight(content) {
  const kernalEl = document.getElementById('notebook-kernal')
  const kernalLang = kernalEl?.getAttribute('data-notebook-kernal')
  const shouldHighlight = kernalEl?.getAttribute('data-highlight')
  const codeNodes = Array.from(content.querySelectorAll(codeElementSelector))
  return {
    shouldHighlight: codeNodes.length > 0 && shouldHighlight !== 'False',
    lang: kernalLang || null,
    nodes: codeNodes,
  }
}

// Python notebooks do not always have languages defined in their metadata.
// For now, we assume that language will be python.
function normalizeKernalLang(lang) {
  return lang === 'undefined' || !lang ? defaultLang : lang
}

async function configureHljs({languages, nodeCount, highlightFn}) {
  return new Promise(async resolve => {
    const langs = await Promise.all(languages.map(langName => commonLanguages[langName]()))

    hljs.configure({
      ignoreUnescapedHTML: true,
      cssSelector: codeElementSelector,
      languages,
    })

    for (const [index, lang] of languages.entries()) {
      hljs.registerLanguage(lang, langs[index].default)
    }

    let highlightedNodes = 0

    hljs.addPlugin({
      'after:highlight': () => {
        highlightedNodes += 1

        if (highlightedNodes >= nodeCount) {
          resolve()
        }
      },
    })

    highlightFn()
  })
}

function highlightNodes(nodes, language) {
  const langClass = `language-${language}`
  for (const node of nodes) {
    // highlightjs tries to 'infer' if content in a node is an instance of the language registered above.
    // This can fail, resulting in valid e.g. python code that is unhighlighted. So, we force the correct class.
    if (!node.classList.contains(langClass)) {
      node.classList.add(langClass)
    }

    hljs.highlightElement(node)
  }
}

async function highlight({content}) {
  const {shouldHighlight, kernalLang, nodes} = needsHighlight(content)

  if (!shouldHighlight) {
    return
  }

  const normalizedKernalLang = normalizeKernalLang(kernalLang)

  // Something went wrong, and we failed to get a valid language.
  if (!(normalizedKernalLang in commonLanguages)) {
    throw new UndefinedLanguageError()
  }

  return configureHljs({
    languages: [normalizedKernalLang],
    nodeCount: nodes.length,
    highlightFn() {
      highlightNodes(nodes, normalizedKernalLang)
    },
  })
}

export {highlight}
